import { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import styles from "./Register.module.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  registerUser,
  clearError,
  clearRegisterSuccess,
} from "../../../store/slices/authSlice";
// import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
// import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { toast } from "react-toastify";

const EMAIL_EXISTS_KEYWORDS = [  
  "already",
  "taken",
  "exists",
  "موجود",
  "مسجل",
  "duplicate",
  "registered",
];
const isEmailExistsError = (err) => {
  if (!err) return false;
  const msg =
    typeof err === "string"
      ? err
      : typeof err === "object"
        ? Object.values(err).flat().join(" ")
        : "";
  return EMAIL_EXISTS_KEYWORDS.some((kw) =>
    msg.toLowerCase().includes(kw.toLowerCase()),
  );
};

export default function RegisterPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, error, registerSuccess } = useSelector((s) => s.auth);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
  });
  const [phoneValue, setPhoneValue] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    password_confirmation: "",
  });

  useEffect(() => {
    if (!registerSuccess) return;
    dispatch(clearRegisterSuccess());
    navigate("/verify-otp", { state: { email: formData.email.trim() } });
  }, [registerSuccess]);

  useEffect(() => {
    if (!error) return;

    if (isEmailExistsError(error)) {
      setErrors((p) => ({
        ...p,
        email: "هذا البريد الإلكتروني مسجّل بالفعل، جرب بريدًا آخر",
      }));
      dispatch(clearError());
      return;
    }

    const msg =
      typeof error === "string"
        ? error
        : typeof error === "object"
          ? Object.values(error).flat().join(" | ")
          : "حدث خطأ غير متوقع";

    toast.error(` ${msg}`, { position: "top-center" });
    dispatch(clearError());
  }, [error, dispatch]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name])
      setErrors((p) => ({ ...p, [e.target.name]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const newErrors = {
      name: "",
      email: "",
      phone: "",
      password: "",
      password_confirmation: "",
    };
    if (!formData.name.trim()) newErrors.name = "الاسم مطلوب";
    if (!formData.email.trim()) newErrors.email = "البريد الإلكتروني مطلوب";
    if (!phoneValue) newErrors.phone = "رقم الهاتف مطلوب";
    if (formData.password.length < 8)
      newErrors.password = "كلمة المرور 8 أحرف على الأقل";
    if (formData.password !== formData.password_confirmation)
      newErrors.password_confirmation = "كلمتا المرور غير متطابقتين";
    setErrors(newErrors);
    return Object.values(newErrors).every((v) => v === "");
  };

const handleSubmit = (e) => {
  e.preventDefault();
  if (!validate()) return;

  const data = new FormData();
  data.append("name", formData.name.trim());
  data.append("email", formData.email.trim());
  data.append("password", formData.password);
  data.append("password_confirmation", formData.password_confirmation);
  data.append("country_code", countryCode); 

  const dialCode = countryCode.replace("+", ""); 
  const purePhone = phoneValue.startsWith(dialCode)
    ? phoneValue.slice(dialCode.length)
    : phoneValue;

  data.append("phone", purePhone); 

  if (imageFile) data.append("image", imageFile);

  dispatch(registerUser(data));
};

  return (
    <div className={styles.register}>
      <div className={styles.RegisterContainer}>
        <div className={styles.title}>
          <img src="/os-logo.png" alt="logo" />
          <h3>بوابة التوقعات الذكية</h3>
        </div>

        <div className={styles.RegisterCard}>
          <h2>{t("register.title")}</h2>
          <p className={styles.desc}>{t("register.desc")}</p>

          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.avatarUpload}>
              <div className={styles.avatarWrapper}>
                <label htmlFor="image" className={styles.avatarLabel}>
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="preview"
                      className={styles.avatarImg}
                    />
                  ) : (
                    <div className={styles.avatarPlaceholder}>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                  )}
                  <div className={styles.avatarOverlay}>
                    <Eye size={20} />
                  </div>
                </label>

                <label htmlFor="image" className={styles.cameraIcon}>
                  <svg
                    width="16"
                    height="16"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                  >
                    <path d="M10.5 8.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
                    <path d="M2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828.828A2 2 0 0 1 3.172 4H2zm.5 2a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm9 2.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0z" />
                  </svg>
                </label>
              </div>
              <input
                type="file"
                id="image"
                name="image"
                accept="image/*"
                className={styles.fileInput}
                onChange={handleImageChange}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="name">{t("register.fullName")}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t("register.fullNamePlaceholder")}
                className={errors.name ? styles.inputError : ""}
              />
              {errors.name && (
                <span className={styles.fieldError}>{errors.name}</span>
              )}
            </div>
            <div className="row">
              <div className={`${styles.inputGroup} col-12 col-md-6`}>
                <label htmlFor="email">{t("register.email")}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? styles.inputError : ""}
                />
                {errors.email && (
                  <span className={styles.fieldError}>{errors.email}</span>
                )}
              </div>

              <div className={`${styles.inputGroup} col-12 col-md-6`}>
                <label htmlFor="phone">{t("register.phone")}</label>
                <div className={errors.phone ? styles.phoneError : ""}>
                  <PhoneInput
  country={"sa"}
  value={phoneValue}
  onChange={(phone, country) => {
    setPhoneValue(phone);

    setCountryCode(`+${country.dialCode}`);

    if (errors.phone) {
      setErrors((p) => ({ ...p, phone: "" }));
    }
  }}
  enableSearch={true}
  disableSearchIcon={true}
  searchPlaceholder="Search country..."
  containerClass={styles.phoneContainer}
  inputClass="form-control"
  buttonClass="phoneButton"
  dropdownClass="phoneDropdown"
/>
                </div>
                {errors.phone && (
                  <span className={styles.fieldError}>{errors.phone}</span>
                )}
              </div>
            </div>
            <div className="row">
              <div
                className={`${styles.inputGroup} ${styles.passwordGroup} col-12 col-md-6`}
              >
                <label htmlFor="password">{t("register.password")}</label>
                <div className={styles.passwordWrap}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={errors.password ? styles.inputError : ""}
                  />
                  <span
                    className={styles.togglePassword}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </span>
                </div>
                {errors.password && (
                  <span className={styles.fieldError}>{errors.password}</span>
                )}
              </div>

              <div
                className={`${styles.inputGroup} ${styles.passwordGroup} col-12 col-md-6`}
              >
                <label htmlFor="confirmPassword">
                  {t("register.confirmPassword")}
                </label>
                <div className={styles.passwordWrap}>
                  <input
                    type={showConfirm ? "text" : "password"}
                    id="confirmPassword"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    className={
                      errors.password_confirmation ? styles.inputError : ""
                    }
                  />
                  <span
                    className={styles.togglePassword}
                    onClick={() => setShowConfirm(!showConfirm)}
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </span>
                </div>
                {errors.password_confirmation && (
                  <span className={styles.fieldError}>
                    {errors.password_confirmation}
                  </span>
                )}
              </div>
            </div>
            <button
              type="submit"
              className={`${styles.button} ${styles.buttonRegister}`}
              disabled={loading}
            >
              {loading ? " جاري إنشاء الحساب..." : t("register.submit")}
            </button>
          </form>

          <p className={styles.signup}>
            {t("register.haveAccount")}{" "}
            <button
              type="button"
              className={styles.joinNow}
              onClick={() => navigate("/login")}
            >
              {t("register.login")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
